import type { CCPreset } from '~/types/presets';
import type { MidiCC } from '~/types/midi';
import { getDefaultHardwareProfile } from '~/lib/profiles/defaultHardwareProfile';

/**
 * Default CC preset for external MIDI hardware
 *
 * This preset provides a safe, generic set of MIDI CC parameters that work
 * well with most hardware synthesizers. Based on General MIDI CC standards
 * and common synthesizer mappings.
 *
 * Key characteristics:
 * - Conservative scramble ranges to prevent extreme/unusable sounds
 * - Focus on universally supported CCs (filter, envelope)
 * - Volume and expression disabled by default for safety
 * - Follows MIDI CC standard: https://www.midi.org/specifications-old/item/table-3-control-change-messages-data-bytes-2
 */

const defaultHardwareCCs: MidiCC[] = [
  // Standard MIDI CCs (disabled for safety)
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },

  // Filter parameters (widely supported)
  { number: 74, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 71, value: 40, scramble: true, smooth: false, show: true, min: 0, max: 127 },

  // Amp Envelope (ADSR)
  { number: 73, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 75, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 70, value: 100, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 72, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },

  // Filter Envelope
  { number: 16, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 17, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 18, value: 100, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 19, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 20, value: 40, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 21, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },

  // Oscillator/Sound Controllers
  { number: 76, value: 64, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 77, value: 64, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 78, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
];

/**
 * Default Hardware MIDI CC Preset
 */
export const DEFAULT_HARDWARE_PRESET: CCPreset = {
  id: 'default-hardware',
  name: 'Default Hardware',
  deviceType: 'MIDI',
  cc: defaultHardwareCCs,
  profiles: [getDefaultHardwareProfile()],
  activeProfileId: 'default-hardware',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the default hardware MIDI CC preset
 * Returns a deep copy to avoid mutation
 */
export function getDefaultHardwarePreset(): CCPreset {
  return {
    ...DEFAULT_HARDWARE_PRESET,
    cc: DEFAULT_HARDWARE_PRESET.cc.map(cc => ({ ...cc })),
    profiles: DEFAULT_HARDWARE_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
